import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilterBar, { type FilterGroup } from "@/components/feature/FilterBar";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import type { DropdownOption } from "@/components/base/Dropdown";
import { movies } from "@/mocks/movies";
import {
  collectOptions,
  filterMovies,
  getByType,
  getCategoryMeta,
  type CategoryKey,
} from "@/utils/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 12;

const sortOptions: DropdownOption[] = [
  { value: "latest", label: "按最新更新" },
  { value: "hot", label: "按播放热度" },
  { value: "rating", label: "按评分最高" },
];

const ALL = "全部";

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = getCategoryMeta(type || "");

  useDocumentMeta(
    meta ? `${meta.name} - 爱看视频` : "影视分类 - 爱看视频",
    meta
      ? `爱看视频提供丰富的${meta.name}影视资源，最新最热尽在其中。`
      : "爱看视频影视分类页面，浏览各类影视资源。"
  );

  const list = useMemo(() => {
    if (!meta) return [];
    return getByType(meta.key as CategoryKey);
  }, [meta]);

  const options = useMemo(() => collectOptions(list), [list]);

  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [genre, setGenre] = useState(ALL);
  const [sort, setSort] = useState<"latest" | "hot" | "rating">("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setSort("latest");
    setVisible(PAGE_SIZE);
  }, [type]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [region, year, genre, sort]);

  const filtered = useMemo(
    () => filterMovies(list, { region, year, genre, sort }),
    [list, region, year, genre, sort]
  );

  const shown = filtered.slice(0, visible);

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1">
          <EmptyState
            icon="ri-error-warning-line"
            title="该分类不存在"
            description="您访问的分类暂未开放，请从导航中选择其他分类"
          />
          <div className="flex justify-center pb-16">
            <Link
              to="/"
              className="px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const groups: FilterGroup[] = [
    {
      key: "genre",
      label: "类型",
      options: [ALL, ...options.genres],
      value: genre,
      onChange: setGenre,
    },
    {
      key: "region",
      label: "地区",
      options: [ALL, ...options.regions],
      value: region,
      onChange: setRegion,
    },
    {
      key: "year",
      label: "年份",
      options: [ALL, ...options.years, "更早"],
      value: year,
      onChange: setYear,
    },
  ];

  const resetFilters = () => {
    setRegion(ALL);
    setYear(ALL);
    setGenre(ALL);
    setSort("latest");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center flex-wrap gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line text-base text-foreground-300"></i>
            <span className="text-foreground-800 font-medium">{meta.name}</span>
          </nav>

          <div className="mt-5 flex items-start gap-3">
            <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 shrink-0">
              <i className={`${meta.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                {meta.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{meta.description}</p>
            </div>
          </div>

          <div className="mt-6">
            <FilterBar
              groups={groups}
              sortOptions={sortOptions}
              sortValue={sort}
              onSortChange={(value) => setSort(value as "latest" | "hot" | "rating")}
              onReset={resetFilters}
            />
          </div>

          <p className="mt-5 text-sm text-foreground-500">
            共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 部{meta.name}
          </p>

          {shown.length === 0 ? (
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到匹配内容"
              description="当前筛选条件下没有相关影视，试试调整或重置筛选条件"
              actionText="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} className="animate-fade-up" />
                ))}
              </div>

              {visible < filtered.length ? (
                <div className="flex justify-center py-10">
                  <button
                    type="button"
                    onClick={() => setVisible((count) => count + PAGE_SIZE)}
                    className="px-8 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm font-medium text-foreground-800 cursor-pointer hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    加载更多（{Math.min(PAGE_SIZE, filtered.length - visible)} 部）
                    <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                </div>
              ) : (
                <p className="text-center py-10 text-sm text-foreground-400">
                  已经到底啦，共 {filtered.length} 部{meta.name}
                </p>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}